<template>
  <div id="leaderHall">
    <div class="dialog">
      <span class="cls" @click="oth_showLeaderHall(false)"></span>

      <div class="paper">
        <!-- Header -->
        <div class="header">
          <h1>🏆 이달의 명예의 전당 <span class="date-badge">{{ currentPeriodLabel }}</span></h1>
          <button class="close-btn" @click="oth_showLeaderHall(false)">
            <i class="ph-bold ph-x"></i>
          </button>
        </div>

        <!-- Chapter Tabs -->
        <div class="chapter-tabs">
          <button :class="{ active: useChapter === 'rank' }" @click="useChapter = 'rank'">퍼포먼스</button>
          <button :class="{ active: useChapter === 'award' }" @click="useChapter = 'award'">어워드</button>
          <button :class="{ active: useChapter === 'challenge' }" @click="useChapter = 'challenge'">기네스</button>
        </div>

        <!-- Scrollable Content -->
        <div class="scroll-content">
          
          <!-- ==================== 퍼포먼스 ==================== -->
          <template v-if="useChapter === 'rank'">
            <!-- Stage Section (Top 3) -->
            <section class="stage-section">
              <div class="stage-bg"></div>
              <div class="podium">
                <!-- 2등 -->
                <div class="podium-slot second">
                  <div class="winner-card" v-if="currentRankings[1]">
                    <div class="card-inner">
                      <div class="avatar">
                        <img :src="currentRankings[1]?.image || '/imgs/unknow_user.png'" />
                      </div>
                      <p class="name">{{ currentRankings[1]?.mb_name }} 설계사</p>
                      <p class="branch">{{ currentRankings[1]?.branch || '본사' }} 지점</p>
                      <div class="stats">
                        <span>이번달 {{ formatScoreShort(currentRankings[1]) }} 체결</span>
                      </div>
                    </div>
                    <div class="rank-badge">2</div>
                  </div>
                  <div class="winner-card empty" v-else>
                    <div class="card-inner">
                      <div class="avatar empty"></div>
                      <p class="name">-</p>
                    </div>
                    <div class="rank-badge">2</div>
                  </div>
                </div>

                <!-- 1등 -->
                <div class="podium-slot first">
                  <div class="winner-card" v-if="currentRankings[0]">
                    <div class="card-inner">
                      <div class="avatar">
                        <img :src="currentRankings[0]?.image || '/imgs/unknow_user.png'" />
                      </div>
                      <p class="name">{{ currentRankings[0]?.mb_name }} 설계사</p>
                      <p class="branch">{{ currentRankings[0]?.branch || '본사' }} 지점</p>
                      <div class="stats">
                        <span>이번달 {{ formatScoreShort(currentRankings[0]) }} 체결</span>
                        <span class="highlight">🏆 이 달의 실적왕</span>
                      </div>
                    </div>
                    <div class="rank-badge">1</div>
                  </div>
                  <div class="winner-card empty" v-else>
                    <div class="card-inner">
                      <div class="avatar empty"></div>
                      <p class="name">-</p>
                    </div>
                    <div class="rank-badge">1</div>
                  </div>
                </div>

                <!-- 3등 -->
                <div class="podium-slot third">
                  <div class="winner-card" v-if="currentRankings[2]">
                    <div class="card-inner">
                      <div class="avatar">
                        <img :src="currentRankings[2]?.image || '/imgs/unknow_user.png'" />
                      </div>
                      <p class="name">{{ currentRankings[2]?.mb_name }} 설계사</p>
                      <p class="branch">{{ currentRankings[2]?.branch || '본사' }} 지점</p>
                      <div class="stats">
                        <span>이번달 {{ formatScoreShort(currentRankings[2]) }} 체결</span>
                      </div>
                    </div>
                    <div class="rank-badge">3</div>
                  </div>
                  <div class="winner-card empty" v-else>
                    <div class="card-inner">
                      <div class="avatar empty"></div>
                      <p class="name">-</p>
                    </div>
                    <div class="rank-badge">3</div>
                  </div>
                </div>
              </div>
            </section>

            <!-- My Progress Section -->
            <section class="info-section">
              <div class="info-card">
                <div class="info-left">
                  <h3>👑 다음 주인공은 나?</h3>
                  <div class="my-progress">
                    <div class="progress-row">
                      <span class="label">현재 순위</span>
                      <span class="value">{{ myRank }}위</span>
                      <span class="arrow">→</span>
                      <span class="label">목표</span>
                      <span class="value highlight">TOP 3</span>
                    </div>
                    <div class="progress-bar-wrap">
                      <div class="progress-bar">
                        <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                      </div>
                      <span class="percent">{{ progressPercent }}%</span>
                    </div>
                    <p class="hint">TOP 3까지 <strong>{{ gapToTop3 }}건</strong> 남았습니다<br><small>(평균 {{ avgDaysToTop3 }}일 소요)</small></p>
                  </div>
                </div>
                <div class="info-right">
                  <h3>💡 이번 TOP 3 설계사들의 공통점</h3>
                  <p class="desc">
                    이달의 실적왕도 처음부터 1위는 아니었습니다.<br><br>
                    꾸준히 기록하고,<br>
                    고객을 놓치지 않았을 뿐입니다.<br><br>
                    우리 모두, 그 길 위에 있습니다.
                  </p>
                  <button class="cta-btn" @click="goToCustomers">오늘 내가 챙길 고객 보기 →</button>
                </div>
              </div>
            </section>

            <!-- Ranking Section -->
            <section class="ranking-section">
              <div class="ranking-header">
                <h3>🏅 랭킹</h3>
                <div class="ranking-filters">
                  <div class="filter-group">
                    <select v-model="selectedBranch">
                      <option value="">전체 지점</option>
                      <option value="본사">본사 지점</option>
                      <option value="논산">논산 지점</option>
                    </select>
                  </div>
                  <div class="filter-tabs">
                    <button :class="{ active: rankType === 'sales' }" @click="rankType = 'sales'">체결순</button>
                    <button :class="{ active: rankType === 'activity' }" @click="rankType = 'activity'">활동순</button>
                    <button :class="{ active: rankType === 'eval' }" @click="rankType = 'eval'">평가순</button>
                  </div>
                </div>
              </div>

              <ul class="ranking-list">
                <li v-for="(member, index) in filteredRankings" :key="member.mb_id">
                  <span class="rank">{{ index + 1 }}</span>
                  <div class="avatar sm">
                    <img :src="member.image || '/imgs/unknow_user.png'" />
                  </div>
                  <div class="member-info">
                    <span class="name">{{ member.mb_name }} 설계사</span>
                    <span class="branch">{{ member.branch || '본사' }} 지점</span>
                  </div>
                  <div class="member-stats">
                    <span class="stat-label">자필가이드</span>
                    <div class="stars">
                      <span v-for="n in 5" :key="n" :class="{ filled: n <= getStarRating(member) }">★</span>
                    </div>
                  </div>
                  <div class="member-score">
                    <span class="score-label">성과평가</span>
                    <span class="score-value">{{ getGrade(member) }}</span>
                  </div>
                </li>
                <li class="empty-row" v-if="filteredRankings.length === 0">
                  <p>데이터가 없습니다</p>
                </li>
              </ul>
            </section>

            <!-- History Section -->
            <section class="history-section">
              <h3>지난 명예의 전당 ▼</h3>
              <ul class="history-list">
                <li v-for="history in pastHallOfFame" :key="history.date">
                  <span class="history-date">🏆 {{ history.date }}</span>
                  <span class="history-winners">
                    {{ history.first }} / {{ history.second }} / {{ history.third }}
                  </span>
                </li>
                <li v-if="pastHallOfFame.length === 0" class="empty-row">
                  <p>이전 기록 없음</p>
                </li>
              </ul>
            </section>
          </template>

          <!-- ==================== 어워드 ==================== -->
          <template v-if="useChapter === 'award'">
            <section class="award-section">
              <div class="year-selector">
                <select v-model="selectedYear" @change="fetchData">
                  <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}년</option>
                </select>
              </div>

              <div class="award-grid">
                <div class="award-card" v-for="type in awardTypes" :key="type.key">
                  <div class="award-header">
                    <span class="award-icon">{{ type.icon }}</span>
                    <div class="award-meta">
                      <h4>{{ type.label }}</h4>
                      <small>{{ type.desc }}</small>
                    </div>
                  </div>
                  <ul class="award-list" v-if="getAwardsByType(type.key).length > 0">
                    <li v-for="award in getAwardsByType(type.key)" :key="award.idx">
                      <div class="avatar sm">
                        <img :src="award.image || '/imgs/unknow_user.png'" />
                      </div>
                      <div class="award-info">
                        <span class="name">{{ award.mb_name }}</span>
                        <span class="date">{{ formatDate(award.award_date) }}</span>
                      </div>
                    </li>
                  </ul>
                  <div class="award-empty" v-else>
                    <p>수상자 없음</p>
                  </div>
                </div>
              </div>
            </section>
          </template>

          <!-- ==================== 기네스 ==================== -->
          <template v-if="useChapter === 'challenge'">
            <section class="guinness-section">
              <p class="guinness-intro">역대 최고 기록을 보유한 설계사들입니다 🎖️</p>

              <div class="guinness-grid">
                <div class="guinness-card" v-for="record in guinnessTypes" :key="record.key">
                  <div class="guinness-icon">{{ record.icon }}</div>
                  <h4>{{ record.label }}</h4>
                  <template v-if="getGuinnessRecord(record.key)">
                    <div class="avatar md">
                      <img :src="getGuinnessRecord(record.key)?.image || '/imgs/unknow_user.png'" />
                    </div>
                    <span class="holder-name">{{ getGuinnessRecord(record.key)?.mb_name }}</span>
                    <span class="holder-value">{{ formatGuinnessValue(record.key, getGuinnessRecord(record.key)?.record_value) }}</span>
                    <span class="holder-date">{{ getGuinnessRecord(record.key)?.record_date }}</span>
                  </template>
                  <template v-else>
                    <div class="avatar md empty"></div>
                    <span class="holder-name">-</span>
                    <span class="holder-value">기록 대기중</span>
                  </template>
                </div>
              </div>
            </section>
          </template>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject } from 'vue';
import { useBackButton } from '@/utils/useBackBtn';

export default {
  name: 'dialog_leaderHall',
  props: {
    oth_showLeaderHall: {
      type: Function,
      required: true
    }
  },
  setup(props) {
    const sql_query = inject('sql_query');
    const member_info = inject('member_info');

    const useChapter = ref('rank');
    const rankType = ref('sales');
    const selectedBranch = ref('');
    const selectedYear = ref(new Date().getFullYear());
    const rankings = ref([]);
    const awards = ref([]);
    const guinness = ref([]);
    const pastHallOfFame = ref([]);

    // 현재 년월
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    const currentPeriodLabel = computed(() => `${currentYear}.${String(currentMonth).padStart(2, '0')}`);

    // 현재 월의 시작일과 마지막일 계산
    const monthStart = computed(() => {
      return `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
    });

    const monthEnd = computed(() => {
      const lastDay = new Date(currentYear, currentMonth, 0).getDate();
      return `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    });

    const yearOptions = computed(() => {
      const options = [];
      for (let i = 0; i < 5; i++) options.push(currentYear - i);
      return options;
    });

    const myRank = computed(() => {
      const idx = currentRankings.value.findIndex(r => r.mb_id === member_info.mb_id);
      return idx >= 0 ? idx + 1 : currentRankings.value.length + 1;
    });

    const gapToTop3 = computed(() => {
      if (currentRankings.value.length < 3) return 0;
      const myData = currentRankings.value.find(r => r.mb_id === member_info.mb_id);
      const third = currentRankings.value[2];
      if (!myData || !third) return 0;
      const myScore = getScoreByType(myData);
      const thirdScore = getScoreByType(third);
      return Math.max(0, (thirdScore || 0) - (myScore || 0) + 1);
    });

    const progressPercent = computed(() => {
      if (currentRankings.value.length < 3) return 0;
      const myData = currentRankings.value.find(r => r.mb_id === member_info.mb_id);
      const third = currentRankings.value[2];
      if (!myData || !third) return 0;
      const myScore = getScoreByType(myData);
      const thirdScore = getScoreByType(third);
      if (!thirdScore) return 0;
      return Math.min(100, Math.round((myScore || 0) / thirdScore * 100));
    });

    const avgDaysToTop3 = computed(() => Math.max(1, Math.ceil(gapToTop3.value / 2)));

    const currentRankings = computed(() => {
      if (!rankings.value || !Array.isArray(rankings.value)) return [];
      const list = [...rankings.value];
      return sortByType(list);
    });

    const filteredRankings = computed(() => {
      let list = currentRankings.value;
      if (selectedBranch.value) {
        list = list.filter(m => m.branch === selectedBranch.value);
      }
      return list;
    });

    const awardTypes = [
      { key: 'MVP', label: 'MVP', icon: '🏅', desc: '이달의 우수사원' },
      { key: 'GOLD', label: 'Gold', icon: '🥇', desc: '150만원 이상' },
      { key: 'SILVER', label: 'Silver', icon: '🥈', desc: '100만원 이상' },
      { key: 'BRONZE', label: 'Bronze', icon: '🥉', desc: '60만원 이상' },
      { key: 'EXEMPLARY', label: 'Exemplary', icon: '⚡', desc: '최고 활동상' },
      { key: 'SMILE', label: 'Smile', icon: '😊', desc: '고객사랑상' },
    ];

    const guinnessTypes = [
      { key: 'SALES', label: '최고 매출', icon: '💰' },
      { key: 'CONTRACTS', label: '최다 계약', icon: '📝' },
      { key: 'CONSULTATIONS', label: '최다 상담', icon: '💬' },
      { key: 'SALARY', label: '최대 급여', icon: '💵' },
      { key: 'RECRUITING', label: '최다 리쿠르팅', icon: '👥' },
    ];

    function getScoreByType(member) {
      if (!member) return 0;
      if (rankType.value === 'sales') return member.sales_score || 0;
      if (rankType.value === 'activity') return member.activity_score || 0;
      return member.eval_score || 0;
    }

    function sortByType(list) {
      if (rankType.value === 'sales') {
        return list.sort((a, b) => (b.sales_score || 0) - (a.sales_score || 0));
      } else if (rankType.value === 'activity') {
        return list.sort((a, b) => (b.activity_score || 0) - (a.activity_score || 0));
      }
      return list.sort((a, b) => (b.eval_score || 0) - (a.eval_score || 0));
    }

    function formatScoreShort(member) {
      if (!member) return '-';
      const score = member.sales_score || 0;
      if (score >= 10000) return Math.floor(score / 10000) + '억원';
      if (score >= 1000) return Math.floor(score / 1000) + '천만원';
      if (score >= 100) return Math.floor(score / 100) + '백만원';
      return score + '만원';
    }

    function formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
    }

    function getStarRating(member) {
      const score = getScoreByType(member);
      if (score >= 50) return 5;
      if (score >= 40) return 4;
      if (score >= 30) return 3;
      if (score >= 20) return 2;
      if (score >= 10) return 1;
      return 0;
    }

    function getGrade(member) {
      const score = getScoreByType(member);
      if (score >= 50) return 'S';
      if (score >= 40) return 'A+';
      if (score >= 30) return 'A';
      if (score >= 20) return 'B+';
      if (score >= 10) return 'B';
      return 'C';
    }

    function getAwardsByType(type) {
      if (!awards.value || !Array.isArray(awards.value)) return [];
      return awards.value.filter(a => a.award_type === type);
    }

    function getGuinnessRecord(type) {
      if (!guinness.value || !Array.isArray(guinness.value)) return null;
      return guinness.value.find(g => g.record_type === type);
    }

    function formatGuinnessValue(type, value) {
      if (!value) return '-';
      if (type === 'SALES' || type === 'SALARY') return Number(value).toLocaleString() + '원';
      return Number(value).toLocaleString() + '건';
    }

    function goToCustomers() {
      props.oth_showLeaderHall(false);
      window.dispatchEvent(new CustomEvent('navigate-to-customers'));
    }

    async function fetchData() {
      // 이번 달 랭킹 조회 (월 범위로 검색)
      const rankSql = `
        SELECT 
          ms.mb_id, 
          m.name as mb_name, 
          m.image,
          m.\`group\` as branch,
          SUM(ms.sales_score) as sales_score,
          SUM(ms.activity_score) as activity_score,
          SUM(ms.eval_score) as eval_score
        FROM member_scores ms
        JOIN member m ON ms.mb_id = m.mb_id
        WHERE ms.score_date >= '${monthStart.value}' 
          AND ms.score_date <= '${monthEnd.value}'
        GROUP BY ms.mb_id, m.name, m.image, m.\`group\`
        ORDER BY sales_score DESC
        LIMIT 50
      `;

      try {
        const rankResult = await sql_query(rankSql);
        rankings.value = Array.isArray(rankResult) ? rankResult : [];
      } catch (e) {
        console.error('Rankings fetch error:', e);
        rankings.value = [];
      }

      // 어워드 조회
      const awardSql = `
        SELECT ma.*, m.name as mb_name, m.image
        FROM member_awards ma
        JOIN member m ON ma.mb_id = m.mb_id
        WHERE YEAR(ma.award_date) = ${selectedYear.value}
        ORDER BY ma.award_date DESC
      `;
      try {
        const awardResult = await sql_query(awardSql);
        awards.value = Array.isArray(awardResult) ? awardResult : [];
      } catch (e) {
        console.error('Awards fetch error:', e);
        awards.value = [];
      }

      // 기네스 조회
      const guinnessSql = `
        SELECT mg.*, m.name as mb_name, m.image
        FROM member_guinness mg
        JOIN member m ON mg.mb_id = m.mb_id
      `;
      try {
        const guinnessResult = await sql_query(guinnessSql);
        guinness.value = Array.isArray(guinnessResult) ? guinnessResult : [];
      } catch (e) {
        console.error('Guinness fetch error:', e);
        guinness.value = [];
      }

      // 지난 명예의 전당 조회 (임시 데이터)
      const historyData = [];
      for (let i = 1; i <= 4; i++) {
        const pastDate = new Date(currentYear, currentMonth - 1 - i, 1);
        const y = pastDate.getFullYear();
        const m = pastDate.getMonth() + 1;
        historyData.push({
          date: `${y}.${String(m).padStart(2, '0')}`,
          first: '김OO',
          second: '김OO', 
          third: '김OO'
        });
      }
      pastHallOfFame.value = historyData;
    }

    useBackButton(() => props.oth_showLeaderHall(false));

    onMounted(() => {
      fetchData();
    });

    return {
      useChapter,
      rankType,
      selectedBranch,
      selectedYear,
      yearOptions,
      rankings,
      currentRankings,
      filteredRankings,
      awards,
      guinness,
      pastHallOfFame,
      awardTypes,
      guinnessTypes,
      currentPeriodLabel,
      myRank,
      gapToTop3,
      progressPercent,
      avgDaysToTop3,
      formatScoreShort,
      formatDate,
      getStarRating,
      getGrade,
      getAwardsByType,
      getGuinnessRecord,
      formatGuinnessValue,
      goToCustomers,
      fetchData,
    };
  }
}
</script>

<style scoped>
#leaderHall {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  color:#fff;
}

#leaderHall .dialog {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

#leaderHall .cls {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.dialog > .paper {
  position: relative;
  width: 95%;
  max-width: 480px;
  height: 92%;
  background: #000;
  border-radius: var(--radius-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ========== Header ========== */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-surface-reverse);
  flex-shrink: 0;
}

.header h1 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-badge {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  background: var(--color-surface-reverse);
  padding: 4px 10px;
  border-radius: 4px;
}

.close-btn {
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  border-radius: 50%;
  color: #fff;;
  background:transparent;
  font-size: var(--font-size-base);
  transition: all 0.15s;
}

.close-btn:active {
  transform: scale(0.95);
  background: #fff;;
  color: #000;
}

/* ========== Chapter Tabs ========== */
.chapter-tabs {
  display: flex;
  gap: 0;
  padding: 0 16px;
  border-bottom: 1px solid var(--color-surface-reverse);
  flex-shrink: 0;
}

.chapter-tabs button {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  position: relative;
  transition: all 0.15s;
}

.chapter-tabs button.active {
  color: var(--color-brand);
  font-weight: var(--font-weight-bold);
}

.chapter-tabs button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--color-brand);
}

/* ========== Scroll Content ========== */
.scroll-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* ========== Stage Section ========== */
.stage-section {
  position: relative;
  padding: 40px 16px 24px;
  background-image: url('~@/../public/imgs/common/hall_bg.png');
  background-size: cover;
  background-position: center;
  overflow: hidden;
}

.stage-bg {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(ellipse 120% 60% at 50% 0%, rgba(255, 200, 100, 0.15) 0%, transparent 60%),
    repeating-linear-gradient(
      90deg,
      transparent 0px,
      transparent 40px,
      rgba(180, 140, 60, 0.08) 40px,
      rgba(180, 140, 60, 0.08) 42px
    );
  pointer-events: none;
}

.podium {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 8px;
  z-index: 1;
}

.podium-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.winner-card {
  position: relative;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 200, 100, 0.3);
  border-radius: 8px;
  padding: 16px 12px;
  min-width: 100px;
  backdrop-filter: blur(8px);
}

.podium-slot.first .winner-card {
  min-width: 130px;
  padding: 20px 16px;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid rgba(255, 200, 100, 0.5);
  box-shadow: 0 0 30px rgba(255, 180, 50, 0.2);
}

.winner-card.empty {
  opacity: 0.5;
}

.card-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.winner-card .avatar {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-surface-reverse);
  border: 2px solid rgba(255, 200, 100, 0.4);
}

.podium-slot.first .winner-card .avatar {
  width: 72px;
  height: 72px;
}

.winner-card .avatar.empty {
  border-style: dashed;
}

.winner-card .avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.winner-card .name {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: #fff;
  margin: 0;
  text-align: center;
}

.podium-slot.first .winner-card .name {
  font-size: var(--font-size-sm);
}

.winner-card .branch {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

.winner-card .stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.winner-card .stats span {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}

.winner-card .stats .highlight {
  font-size: var(--font-size-xs);
  color: var(--color-brand);
  font-weight: var(--font-weight-bold);
}

.rank-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-brand);
  color: #000;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  border-radius: 50%;
}

.podium-slot.first .rank-badge {
  width: 32px;
  height: 32px;
  font-size: var(--font-size-base);
}

.podium-slot.second .rank-badge {
  background: #C0C0C0;
}

.podium-slot.third .rank-badge {
  background: #CD7F32;
  color: #fff;
}

/* ========== Info Section ========== */
.info-section {
  padding: 16px;
}

.info-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: linear-gradient(180deg, #F3E9D2 0%, #BFA478 100%);
  border-radius: var(--radius-md);
  padding: 20px;
  color:#000;
}

.info-left, .info-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-left h3, .info-right h3 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.my-progress {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.progress-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.progress-row .label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.progress-row .value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
}

.progress-row .value.highlight {
  color: var(--color-brand);
}

.progress-row .arrow {
  color: var(--color-text-third);
}

.progress-bar-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--color-brand), #FFD700);
  border-radius: 4px;
  transition: width 0.4s ease;
}

.percent {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
  min-width: 40px;
}

.hint {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.hint strong {
  color: var(--color-brand);
}

.hint small {
  color: var(--color-text-third);
}

.info-right {
  padding-top: 16px;
  border-top: 1px solid #ddd;
}

.info-right .desc {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin: 0;
}

.cta-btn {
  padding: 12px 16px;
  background: transparent;
  color: #fff;;
  border: 1px solid var(--color-text-third);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s;
  text-align: center;
}

.cta-btn:active {
  background: var(--color-brand);
  border-color: var(--color-brand);
  color: #000;
}

/* ========== Ranking Section ========== */
.ranking-section {
  padding: 16px;
}

.ranking-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.ranking-header h3 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.ranking-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.filter-group select {
  padding: 6px 12px;
  border: 1px solid var(--color-text-third);
  border-radius: 4px;
  background: var(--color-surface-reverse);
  color: #fff;;
  font-size: var(--font-size-xs);
  cursor: pointer;
}

.filter-tabs {
  display: flex;
  gap: 0;
  border: 1px solid var(--color-text-third);
  border-radius: 4px;
  overflow: hidden;
}

.filter-tabs button {
  padding: 6px 12px;
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border-right: 1px solid var(--color-text-third);
  transition: all 0.15s;
}

.filter-tabs button:last-child {
  border-right: none;
}

.filter-tabs button.active {
  background: var(--color-brand);
  color: #000;
}

.ranking-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ranking-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--color-surface-reverse);
  border-radius: var(--radius-md);
}

.ranking-list .rank {
  width: 24px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-secondary);
  text-align: center;
}

.ranking-list .avatar.sm {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: #ddd;
  flex-shrink: 0;
}

.ranking-list .avatar.sm img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.member-info .name {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.member-info .branch {
  font-size: 10px;
  color: var(--color-text-secondary);
}

.member-stats {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.member-stats .stat-label {
  font-size: 9px;
  color: var(--color-text-third);
}

.stars {
  display: flex;
  gap: 1px;
}

.stars span {
  font-size: 10px;
  color: var(--color-text-third);
}

.stars span.filled {
  color: var(--color-brand);
}

.member-score {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.member-score .score-label {
  font-size: 9px;
  color: var(--color-text-third);
}

.member-score .score-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
}

.ranking-list .empty-row,
.history-list .empty-row {
  justify-content: center;
  padding: 32px;
}

.ranking-list .empty-row p,
.history-list .empty-row p {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  margin: 0;
}

/* ========== History Section ========== */
.history-section {
  padding: 16px;
  border-top: 1px solid var(--color-surface-reverse);
}

.history-section h3 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  margin: 0 0 12px;
  text-align: center;
  color: var(--color-text-secondary);
}

.history-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-list li {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 12px;
  background: var(--color-surface-reverse);
  border-radius: var(--radius-md);
}

.history-date {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
  min-width: 80px;
}

.history-winners {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

/* ========== Award Section ========== */
.award-section {
  padding: 16px;
}

.year-selector {
  margin-bottom: 16px;
}

.year-selector select {
  padding: 8px 16px;
  border: 1px solid var(--color-text-third);
  border-radius: 4px;
  background: var(--color-surface-reverse);
  color: #fff;;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
}

.award-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.award-card {
  background: var(--color-surface-reverse);
  border-radius: var(--radius-md);
  padding: 16px;
}

.award-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ddd;
}

.award-icon {
  font-size: 28px;
}

.award-meta h4 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  margin: 0;
}

.award-meta small {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.award-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.award-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  background: #000;
  border-radius: var(--radius-sm);
}

.award-list .avatar.sm {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: #ddd;
  flex-shrink: 0;
}

.award-list .avatar.sm img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.award-info {
  display: flex;
  flex-direction: column;
}

.award-info .name {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
}

.award-info .date {
  font-size: 10px;
  color: var(--color-text-secondary);
}

.award-empty {
  padding: 24px;
  text-align: center;
}

.award-empty p {
  font-size: var(--font-size-xs);
  color: var(--color-text-third);
  margin: 0;
}

/* ========== Guinness Section ========== */
.guinness-section {
  padding: 16px;
}

.guinness-intro {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-align: center;
  margin: 0 0 20px;
}

.guinness-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.guinness-card {
  background: var(--color-surface-reverse);
  border-radius: var(--radius-md);
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}

.guinness-icon {
  font-size: 32px;
}

.guinness-card h4 {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  margin: 0;
}

.guinness-card .avatar.md {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: #ddd;
  border: 2px solid var(--color-brand);
  margin-top: 8px;
}

.guinness-card .avatar.md.empty {
  border-style: dashed;
  border-color: var(--color-text-third);
}

.guinness-card .avatar.md img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.holder-name {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.holder-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  color: var(--color-brand);
}

.holder-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

/* ========== Responsive ========== */
@media (min-width: 768px) {
  .dialog > .paper {
    max-width: 520px;
    height: 88%;
  }

  .info-card {
    flex-direction: row;
  }

  .info-left, .info-right {
    flex: 1;
  }

  .info-right {
    padding-top: 0;
    padding-left: 20px;
    border-top: none;
    border-left: 1px solid #ddd;
  }
}

/* ========== Scroll Content ========== */
.scroll-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

/* Webkit 스크롤바 */
.scroll-content::-webkit-scrollbar {
  width: 6px;
}

.scroll-content::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.scroll-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.35);
}
</style>